import React, { useState } from 'react';
import { Modal } from '../UI';

/**
 * The line items exactly as the requester entered them, read-only.
 *
 * For logistics requisitions pass isLogistics={true} to show the
 * Cost Object Type, Category/Asset Type and Info (logistics additional
 * info) columns.
 */

const fmt = (v) =>
  v != null && !isNaN(v) ? parseFloat(v).toLocaleString('en-NG', { minimumFractionDigits: 2 }) : '—';

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  const [y, m, day] = s.split('-');
  return y && m && day ? `${day}-${m}-${y}` : s;
};

const dash = (v) => (v === null || v === undefined || String(v).trim() === '' ? '—' : v);
const cap = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : v);

const th = {
  padding: '9px 10px', textAlign: 'center', fontWeight: 700, fontSize: 10.5,
  whiteSpace: 'nowrap', color: '#fff', background: '#0b318bff',
  border: '1px solid #cbd5e1',
};
const td = {
  padding: '7px 10px', textAlign: 'center', fontSize: 12,
  border: '1px solid #cbd5e1',
};

const BASE_COLS = [
  ['MATERIAL', 140], ['DESCRIPTION', 190], ['SPEC', 110], ['REQUESTED FOR', 150], ['VENDOR', 140],
  ['PROJECT', 140], ['BOQ', 130], ['SUB BOQ', 130], ['EXPENSE CATEGORY', 140],
  ['SUB EXPENSE CATEGORY', 140], ['BRAND', 100], ['UNIT', 70], ['QTY', 70],
  ['RATE (₦)', 100], ['EST. COST (₦)', 110], ['VAT (%)', 70],
  ['COST CENTRE', 130], ['GL ACCOUNT', 150], ['LEDGER', 130],
  ['REQ. DATE', 100], ['PRIORITY', 90], ['REMARKS', 150],
];

const LOGISTICS_EXTRA_COLS = [
  ['COST OBJECT TYPE', 150],
  ['CATEGORY / ASSET TYPE', 160],
  ['ADD. INFO', 90],
];

// ─────────────────────────────────────────────────────────────
// Read-only modal showing the logistics_additional_info rows
// ─────────────────────────────────────────────────────────────
const LAI_COLS = [
  { key: 'product', label: 'PRODUCT' },
  { key: 'vehicle_number', label: 'VEHICLE NO.' },
  { key: 'last_top_up_date', label: 'LAST TOP-UP', date: true },
  { key: 'gasoline_station', label: 'GASOLINE STATION' },
  { key: 'litres_requested', label: 'LITRES REQ.', num: true },
  { key: 'curr_price_per_litre', label: 'CURR PRICE/L', num: true },
  { key: 'total_amount', label: 'TOTAL AMT', num: true },
  { key: 'prev_mileage', label: 'PREV MILEAGE', num: true },
  { key: 'curr_mileage', label: 'CURR MILEAGE', num: true },
  { key: 'last_price_per_litre', label: 'LAST PRICE/L', num: true },
  { key: 'last_litres', label: 'LAST LITRES', num: true },
  { key: 'others', label: 'OTHERS' },
  { key: 'remarks', label: 'REMARKS' },
];

function LogisticsInfoViewModal({ rows = [], onClose }) {
  const totalLitres = rows.reduce((s, r) => s + (parseFloat(r.litres_requested) || 0), 0);
  const totalFuel   = rows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0);

  const thS = { padding: '8px 6px', background: '#0b318b', color: '#fff', fontSize: 11.5, whiteSpace: 'nowrap', textAlign: 'center', border: '1px solid #cbd5e1' };
  const tdS = { padding: '6px 8px', fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap', border: '1px solid #e5e7eb' };
  const btnClose = { padding: '7px 18px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 };

  return (
    <Modal title="Logistics Additional Information" onClose={onClose} width={1300}>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>
        Supplementary vehicle, fuel &amp; logistics details for this line item
      </p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Total Rows',      val: rows.length },
          { label: 'Total Litres',    val: fmt(totalLitres) },
          { label: 'Total Fuel Cost', val: '₦' + fmt(totalFuel) },
        ].map(s => (
          <div key={s.label} style={{
            flex: 1, background: '#f0f4ff', borderRadius: 8, padding: '10px 16px',
            textAlign: 'center', border: '1px solid black',
          }}>
            <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0b318b', marginTop: 2 }}>{s.val}</div>
          </div>
        ))}
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 6, marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ ...thS, width: 36 }}>#</th>
              {LAI_COLS.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                <td style={tdS}>{i + 1}</td>
                {LAI_COLS.map(c => (
                  <td key={c.key} style={tdS}>
                    {c.num ? fmt(r[c.key]) : c.date ? fmtDate(r[c.key]) : dash(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={LAI_COLS.length + 1} style={{ ...tdS, color: '#9ca3af', padding: 20 }}>
                  No additional logistics info on this line.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={btnClose}>Close</button>
      </div>
    </Modal>
  );
}

export default function RequestedLineItems({ items = [], isLogistics = false }) {
  const [infoModal, setInfoModal] = useState(null); // logistics_additional_info rows to show

  const COLS = isLogistics
    ? [...BASE_COLS, ...LOGISTICS_EXTRA_COLS]
    : BASE_COLS;

  const colCount = COLS.length + 1;

  return (
    <>
      {infoModal !== null && (
        <LogisticsInfoViewModal rows={infoModal} onClose={() => setInfoModal(null)} />
      )}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 8, padding: '0 16px' }}>
          LINE ITEMS AS REQUESTED
        </div>
        <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 34, textAlign: 'center' }}>#</th>
                {COLS.map(([label, min]) => (
                  <th key={label} style={{ ...th, minWidth: min }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.item_id || i}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}>{dash(item.material_name || item.material_other)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{dash(item.description)}</td>
                  <td style={td}>{dash(item.specification)}</td>
                  <td style={td}>{dash(item.item_requested_for_display_name)}</td>
                  <td style={td}>{dash(item.vendor_name || item.vendor_other)}</td>
                  <td style={td}>{dash(item.project_name)}</td>
                  <td style={td}>{dash(item.boq_item_description)}</td>
                  <td style={td}>{dash(item.boq_component_description)}</td>
                  <td style={td}>{dash(item.expense_category_name || item.expense_category_other)}</td>
                  <td style={td}>{dash(item.expense_sub_category_name)}</td>
                  <td style={td}>{dash(item.brand)}</td>
                  <td style={td}>{dash(item.unit_of_measure)}</td>
                  <td style={td}>{fmt(item.quantity)}</td>
                  <td style={td}>{fmt(item.estimated_unit_rate)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmt(item.estimated_amount)}</td>
                  <td style={td}>{item.vat_percentage != null ? `${item.vat_percentage}%` : '—'}</td>
                  <td style={td}>{dash(item.cost_centre_name)}</td>
                  <td style={td}>
                    {dash(item.account_ledger_code
                      ? `${item.account_ledger_code} - ${item.account_ledger_name || ''}`.replace(/ - $/, '')
                      : item.account_ledger_name)}
                  </td>
                  <td style={td}>{dash(item.ledger_name || item.ledger_other)}</td>
                  <td style={td}>{item.req_date ? fmtDate(item.req_date) : '—'}</td>
                  <td style={td}>{dash(cap(item.priority_status))}</td>
                  <td style={td}>{dash(item.remarks)}</td>

                  {/* Logistics-only columns */}
                  {isLogistics && (
                    <>
                      <td style={td}>{dash(item.cost_object_type_name)}</td>
                      <td style={td}>{dash(item.category_asset_type_name)}</td>
                      <td style={{ ...td, padding: '4px 8px' }}>
                        <button
                          onClick={() => setInfoModal(item.logistics_additional_info || [])}
                          style={{
                            padding: '4px 14px', background: '#0b318b', color: '#fff',
                            border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11.5,
                            fontWeight: 600, position: 'relative',
                          }}
                        >
                          Info
                          {(item.logistics_additional_info || []).length > 0 && (
                            <span style={{
                              position: 'absolute', top: -5, right: -5, background: '#ef4444',
                              color: '#fff', borderRadius: '50%', width: 15, height: 15,
                              fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontWeight: 700,
                            }}>
                              {(item.logistics_additional_info || []).length}
                            </span>
                          )}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ ...td, padding: 20, color: 'var(--text-muted)' }}>
                    No line items on this requisition.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
